<template>
  <v-row
    :class="topBarClass"
    class="card"
    align="start"
  >
    <sn-loader
      v-if="loading"
      class="pt-16 pb-16 mt-16 mb-16"
      :loading="true"
      :wait-for-animation="true"
      :speed="1.25"
      :messages="['Please wait...']"
    />

    <v-col
      v-else
      class="action-col"
      cols="12"
    >
      <div
        v-if="topBar"
        class="top-bar"
      >
        <slot name="top-bar-back-button" />
      </div>
      <div
        class="card-content"
      >
        <a
          :href="landingPageLinkUrl"
          :class="{ 'top-illustration': showIllustration, 'company-logo': $vuetify.breakpoint.mdAndDown, 'lead-gen-logo': !$vuetify.breakpoint.mdAndDown && this.$route.path.includes('get-started') }"
          tabindex="5"
        >
          <sn-illustration
            v-if="showIllustration"
            class="mx-auto illustration"
            :illustration="illustration"
          />
          <img
            v-else
            :class="`${$vuetify.breakpoint.mdAndDown ? 'small-logo' : 'large-logo'}`"
            :alt="((this.$store.company && this.$store.company.name) || 'Company') + ' Logo'"
            :src="$vuetify.breakpoint.mdAndDown ? companyAppIconUrl : companyLogoUrl"
          >
        </a>
        <slot />
      </div>
      <div
        v-if="showActionSection"
        class="action-section"
        :style="$vuetify.breakpoint.mdAndDown ? 'transparent' : `background:${profileColorLight}`"
        cols="12"
      >
        <slot name="card-buttons" />
      </div>
    </v-col>
  </v-row>
</template>

<script>
import { rgbaFromHexAndOpacity } from '~/assets/javascript/utilityFunctions.js'
import SnLoader from '~/components/common/snLoader'

export default {
  name: 'FormCard',
  components: { SnLoader },
  props: {
    showActionSection: {
      type: Boolean,
      required: false,
      default: true
    },
    topBar: {
      type: Boolean,
      required: false,
      default: false
    },
    illustration: {
      type: String,
      required: false,
      default: 'SocialSecurity'
    },
    showIllustration: {
      type: Boolean,
      required: false,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    getIllustration () {
      return this.illustration
    },
    topBarClass () {
      return this.topBar ? 'flush-top-bar' : 'gradient-top-bar'
    },
    themeColor () {
      return this.$vuetify.theme.themes.light.primary
    },
    profileColorLight () {
      return rgbaFromHexAndOpacity(this.themeColor, 0.05)
    },
    companyLogoUrl () {
      return this.$store.servicer?.effective_company_logo_url || this.$store.effectiveLogoUrl
    },
    companyAppIconUrl () {
      return this.$store.servicer?.effective_app_icon_url || this.$store.effectiveAppIconUrl
    },
    landingPageLinkUrl () {
      if (!this.$store.activationCode) {
        return '/homehub/login'
      }
      const currentUrl = this.$route.path.split('/')
      const homehubIndex = currentUrl.indexOf('homehub')
      if (currentUrl.includes('onboarding')) {
        currentUrl.splice(homehubIndex + 1, 2, 'signup')
      } else {
        currentUrl.splice(homehubIndex + 1, 1, 'signup')
      }
      const landingPageUrl = currentUrl.join('/')
      return landingPageUrl
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/snui';

.card {
  position: relative;
  display: flex;
  height: fit-content;
  background-color: $sn-white;
  border-radius: 16px;
}
.top-bar {
  display: none;
}
.top-illustration {
  text-align: center;
}
.company-logo {
  width: fit-content;
  align-self: center;
  margin-top: -40px;
}
.lead-gen-logo {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: -40px;
  margin-bottom: 24px;
}
.action-col h1 {
  margin-top: 35px
}
.card-content {
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  height: 100%;
  padding: 60px 44px 44px;
}
.large-logo {
  width: auto;
  height: 54px;
  object-fit: contain;
  object-position: left;
}
.illustration {
  margin-bottom: 24px;
  width: 160px;
  height: 160px;
  object-fit: contain;
  object-position: center;
}
.action-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 38px 56px 32px;
  height: 120px;
  border-radius: 0 0 16px 16px;
  margin: 12px -12px -12px;
  width: calc(100% + 24px);
}

@media (max-width: $medium-screen-size) {
  .card {
    align-items: center;
    padding: 0px 16px 0px 16px;
  }
  .action-col {
    flex: 1;
    margin: 0;
    padding: 0;
    padding-bottom: 16px;
    border-radius: 8px;
  }
  .card-content {
    padding: 44px 0 0 0;
  }
  .small-logo {
    display: initial;
    align-self: center;
    width: 80px;
    height: 80px;
    margin-top: -52px;
    padding: 8px;
    background-color: $sn-white;
    border-radius: 50%;
    box-shadow: $sn-shadow-depth-1;
  }
  .small-logo img {
    width: 48px;
    height: 48px;
  }
  .action-section {
    height: initial;
    width: 100%;
    margin-top: 16px;
    margin-left: 0;
    margin-right: 0;
    padding: 0;
    background-color: inherit;
  }

  .flush-top-bar {
    margin-top: -173px;
    border-radius: unset;

    & .top-bar {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      width: calc(100% + 32px);
      height: 60px;
      margin: 0 -16px;
      padding: 0 16px;
      box-shadow: 0px 8px 14px -4px rgba(0, 0, 0, 0.08);
    }

    & .small-logo {
      width: 40px;
      height: 40px;
      margin-top: -81px;
      margin-bottom: 20px;
      padding: 0;
      border-radius: 0;
      box-shadow: none;

      & img {
        width: 100%;
        height: 100%;
      }
    }
  }
  .gradient-top-bar {
    border-radius: 16px 16px 0 0;
  }
}

@media (min-width: ($medium-screen-size + 1)) {
  .card {
    box-shadow: $sn-shadow-depth-3;
  }
}

</style>
